import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getCurvaS } from '../../services/curvaSService';
import { formatPrice } from '../../utils/formatterUtils';

const monthLabels = ['Gen','Feb','Mar','Apr','Mag','Giu','Lug','Ago','Set','Ott','Nov','Dic'];

export default function CurveForecastSection() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const scChartRef = useRef(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await getCurvaS(id);
        setData(res);
        requestAnimationFrame(() => {
          if (scChartRef.current) scChartRef.current.scrollLeft = scChartRef.current.scrollWidth;
        });
      } catch (err) {
        console.error('Errore recupero curva S', err);
      }
    }
    fetchData();
  }, [id]);

  if (!data) return null;

  const curve = data.curve || [];
  const { forecast } = data;

  // chart dimensions
  const minStep = 60;
  const height = 180;
  const maxVal = Math.max(...curve.map(c => Math.max(c.ricavi, c.costi)), 0);
  const scaleY = maxVal > 0 ? height / maxVal : 0;
  const paddingX = 25;
  const width = Math.max((curve.length - 1) * minStep, 600);
  const stepX = curve.length > 1 ? (width - paddingX * 2) / (curve.length - 1) : 0;
  const ricaviPoints = curve.map((c, i) => `${paddingX + i * stepX},${height - c.ricavi * scaleY}`).join(' ');
  const costiPoints = curve.map((c, i) => `${paddingX + i * stepX},${height - c.costi * scaleY}`).join(' ');

  const labelMax = 5;
  const yStep = maxVal > 0 ? Math.ceil(maxVal / (labelMax - 1)) : 1;
  const yLabels = Array.from({ length: labelMax }, (_, i) => yStep * (labelMax - 1 - i));

  const labelStep = 1;

  const ricaviAttuali = forecast.ricaviAttuali || 0;
  const ricaviPrevisti = forecast.ricaviPrevisti || 0;
  const costiAttuali = forecast.costiAttuali || 0;
  const costiPrevisti = forecast.costiPrevisti || 0;
  const ricaviPerc = ricaviPrevisti > 0 ? (ricaviAttuali / ricaviPrevisti) * 100 : 0;
  const costiPerc = costiPrevisti > 0 ? (costiAttuali / costiPrevisti) * 100 : 0;

  const incRicavi = [];
  const incCosti = [];
  for (let i = Math.max(curve.length - 6, 0); i < curve.length; i++) {
    const prev = i > 0 ? curve[i - 1] : { ricavi: 0, costi: 0 };
    incRicavi.push(curve[i].ricavi - prev.ricavi);
    incCosti.push(curve[i].costi - prev.costi);
  }

  const handleEnter = (e, item) => {
    setTooltip({
      month: `${monthLabels[Number(item.month.split('-')[1]) - 1]} ${item.month.split('-')[0]}`,
      ricavi: item.ricavi,
      costi: item.costi,

      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMove = (e) => {
    if (tooltip) setTooltip({ ...tooltip, x: e.clientX, y: e.clientY });
  };

  const handleLeave = () => setTooltip(null);

  return (
    <section className="grid-analytics">
      <article className="card-dash s-curve-card">
        <h4>Curva S – Cumulato Costi vs Ricavi</h4>
        <div className="sc-legend">
          <span className="dot ricavi"></span><span>Ricavi cumulati (Compreso Box e Varianti)</span>
          <span className="dot costi"></span><span>Costi cumulati (Compreso Banca)</span>
        </div>
        <div className="sc-chart" ref={scChartRef}>
          <svg className="sc-svg" viewBox={`0 0 ${width} 220`}  style={{ width }}>
            <g className="sc-grid">
              {yLabels.slice(1).map((y) => (
                <line key={y} x1={paddingX} y1={height - y * scaleY} x2={width - paddingX} y2={height - y * scaleY} />
              ))}
            </g>
            <polyline className="sc-line-costi" points={costiPoints} />
            <polyline className="sc-line-ricavi" points={ricaviPoints} />
            {curve.map((c, i) => (
              <g key={c.month}>
                <circle
                  className="sc-dot costi"
                  cx={paddingX + i * stepX}
                  cy={height - c.costi * scaleY}
                  r="3"
                  onMouseEnter={(e) => handleEnter(e, c)}

                  onMouseMove={handleMove}
                  onMouseLeave={handleLeave}
                />
                <circle
                  className="sc-dot ricavi"
                  cx={paddingX + i * stepX}
                  cy={height - c.ricavi * scaleY}
                  r="3"
                  onMouseEnter={(e) => handleEnter(e, c)}

                  onMouseMove={handleMove}
                  onMouseLeave={handleLeave}
                />
              </g>
            ))}
            <g className="sc-axis" fill="none">
              {curve.map((c, i) => (
                <text key={c.month} x={paddingX + i * stepX} y="200" textAnchor="middle">
                  {i % labelStep === 0 ? `${monthLabels[Number(c.month.split('-')[1]) - 1]} ${c.month.slice(2,4)}` : ''}
                </text>
              ))}
            </g>
          </svg>
        </div>
        {tooltip && (
          <div
            className="chart-tooltip--curvaS"
            style={{ left: tooltip.x, top: tooltip.y }}
          >
            <div className="tt-title--curvaS">{tooltip.month}</div>
            <div className="tt-row--curvaS">
              <span className="tt-swatch--curvaS tt-ricavi--curvaS"></span>
              Ricavi: <strong>{formatPrice(tooltip.ricavi)}</strong>
            </div>
            <div className="tt-row--curvaS">
              <span className="tt-swatch--curvaS tt-costi--curvaS"></span>
              Costi: <strong>{formatPrice(tooltip.costi)}</strong>
            </div>
          </div>
        )}
      </article>
      <article className="card-dash forecast-card">
        <h4>Proiezione a fine progetto</h4>
        <div className="forecast-grid">
          <div className="fc-block">
            <div className="fc-title">Ricavi cumulati attuali (Compreso Box e Varianti)</div>
            <div className="fc-value">{formatPrice(ricaviAttuali)}</div>
            <div className="fc-sub">Proiezione a fine: {formatPrice(ricaviPrevisti)}</div>
            <div className="fc-track"><span className="fc-ricavi" style={{ width: `${Math.min(ricaviPerc,100)}%` }}></span></div>
            <div className="spark" aria-hidden="true">
              <span  style={{ height: `12px` }}></span>
              <span  style={{ height: `18px` }}></span>
              <span  style={{ height: `28px` }}></span>
              <span  style={{ height: `36px` }}></span>
              <span  style={{ height: `44px` }}></span>
              <span  style={{ height: `50px` }}></span>

            </div>
          </div>
          <div className="fc-block">
            <div className="fc-title">Costi cumulati attuali (Compreso Banca)</div>
            <div className="fc-value">{formatPrice(costiAttuali)}</div>
            <div className="fc-sub">Proiezione a fine: {formatPrice(costiPrevisti)}</div>
            <div className="fc-track"><span className="fc-costi" style={{ width: `${Math.min(costiPerc,100)}%` }}></span></div>
            <div className="spark" aria-hidden="true">
              <span className="costi" style={{ height: `12px` }}></span>
              <span className="costi" style={{ height: `18px` }}></span>
              <span className="costi" style={{ height: `28px` }}></span>
              <span className="costi" style={{ height: `36px` }}></span>
              <span className="costi" style={{ height: `44px` }}></span>
              <span className="costi" style={{ height: `50px` }}></span>
            </div>
          </div>
        </div>
      </article>
    </section>
  );
}
